import { useEffect, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  DropdownV2,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  Text,
  TextAreaInputV2,
} from '@equip.health/ui';

import { MaiFeedbackReason } from '~/lib/types/mai/MaiFeedbackReason';

import {
  MAI_FEEDBACK_COMMENT_LABEL,
  MAI_FEEDBACK_COMMENT_LIMIT,
  MAI_FEEDBACK_MODAL_BODY,
  MAI_FEEDBACK_MODAL_TITLE,
  MAI_FEEDBACK_REASON_LABEL,
  MAI_FEEDBACK_REASON_OPTIONS,
} from './maiFeedback.constants';
import { toMaiFeedbackReason } from './maiFeedback.util';

const TOP_LEVEL_ID = 'mai-chat-feedback';

interface MaiChatFeedbackModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (reason: MaiFeedbackReason, comment?: string) => void;
}

const MaiChatFeedbackModal = ({
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
}: MaiChatFeedbackModalProps) => {
  const [reason, setReason] = useState<MaiFeedbackReason | null>(null);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason(null);
      setComment('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!reason) return;
    const trimmed = comment.trim();
    onSubmit(reason, trimmed || undefined);
  };

  return (
    <ModalV2
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={MAI_FEEDBACK_MODAL_TITLE}
    >
      <ChakraFlex flexDirection="column" gap="24px">
        <Text variant="body">{MAI_FEEDBACK_MODAL_BODY}</Text>
        <InputGroupV2
          id={`${TOP_LEVEL_ID}-reason-group`}
          label={MAI_FEEDBACK_REASON_LABEL}
        >
          <DropdownV2
            ariaLabel="Reason for rating thumbs down"
            id={`${TOP_LEVEL_ID}-reason`}
            isResettable
            menuOptions={MAI_FEEDBACK_REASON_OPTIONS}
            onOptionClick={(option) => setReason(toMaiFeedbackReason(option))}
            placeholder="Select one"
            value={reason ?? undefined}
          />
        </InputGroupV2>
        <ChakraBox marginX="1px">
          <InputGroupV2
            id={`${TOP_LEVEL_ID}-comment-group`}
            isRequired={false}
            label={MAI_FEEDBACK_COMMENT_LABEL}
          >
            <TextAreaInputV2
              ariaLabel="Enter additional feedback here"
              characterLimit={MAI_FEEDBACK_COMMENT_LIMIT}
              id={`${TOP_LEVEL_ID}-comment`}
              onChange={setComment}
              value={comment}
            />
          </InputGroupV2>
        </ChakraBox>
        <ChakraFlex justifyContent="space-between">
          <FireflyButton
            id={`${TOP_LEVEL_ID}-cancel`}
            isDisabled={isSubmitting}
            leftIcon={FAIconName.XmarkRegular}
            onClick={onClose}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}-submit`}
            isDisabled={!reason}
            isLoading={isSubmitting}
            leftIcon={FAIconName.checkRegular}
            onClick={handleSubmit}
          >
            Submit feedback
          </FireflyButton>
        </ChakraFlex>
      </ChakraFlex>
    </ModalV2>
  );
};

export default MaiChatFeedbackModal;
